import { Alert, Badge } from "flowbite-react";

function renderInlineMarkdown(text, keyPrefix) {
  const tokenPattern =
    /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^)]+\))/g;

  return String(text)
    .split(tokenPattern)
    .filter(Boolean)
    .map((part, index) => {
      const key = `${keyPrefix}-${index}`;
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={key}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("`") && part.endsWith("`")) {
        return (
          <code
            key={key}
            className="rounded bg-gray-100 px-1 py-0.5 text-[0.9em] dark:bg-gray-800"
          >
            {part.slice(1, -1)}
          </code>
        );
      }
      const linkMatch = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/);
      if (linkMatch) {
        return (
          <a
            key={key}
            href={linkMatch[2]}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-blue-700 underline underline-offset-2"
          >
            {linkMatch[1]}
          </a>
        );
      }
      return part;
    });
}

export function QuickSearchMarkdown({ children }) {
  const lines = String(children || "").split(/\r?\n/);

  return (
    <div className="space-y-2 text-sm leading-6">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={`space-${index}`} className="h-1" />;

        const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
        if (heading) {
          const className =
            heading[1].length === 1
              ? "text-base font-semibold"
              : "text-sm font-semibold";
          return (
            <div key={`heading-${index}`} className={className}>
              {renderInlineMarkdown(heading[2], `heading-${index}`)}
            </div>
          );
        }

        const bullet = trimmed.match(/^[-*]\s+(.+)$/);
        if (bullet) {
          return (
            <div key={`bullet-${index}`} className="flex gap-2 pl-1">
              <span aria-hidden="true">•</span>
              <div>{renderInlineMarkdown(bullet[1], `bullet-${index}`)}</div>
            </div>
          );
        }

        const numbered = trimmed.match(/^(\d+)\.\s+(.+)$/);
        if (numbered) {
          return (
            <div key={`numbered-${index}`} className="flex gap-2 pl-1">
              <span>{numbered[1]}.</span>
              <div>
                {renderInlineMarkdown(numbered[2], `numbered-${index}`)}
              </div>
            </div>
          );
        }

        return (
          <p key={`paragraph-${index}`}>
            {renderInlineMarkdown(trimmed, `paragraph-${index}`)}
          </p>
        );
      })}
    </div>
  );
}

export function extractGroundingSources(result) {
  const chunks =
    result?.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
  const seen = new Set();

  return chunks
    .map((chunk) => chunk?.web)
    .filter(
      (source) => source?.uri && !seen.has(source.uri) && seen.add(source.uri),
    )
    .map((source) => ({ title: source.title || source.uri, url: source.uri }));
}

export function extractModelText(result) {
  return (
    result?.candidates?.[0]?.content?.parts
      ?.map((part) => part.text)
      .filter(Boolean)
      .join("") || ""
  );
}

export function SourceLinks({ sources }) {
  if (!sources?.length) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {sources.map((source) => (
        <a
          key={source.url}
          href={source.url}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-medium text-blue-700 underline decoration-blue-200 underline-offset-2 hover:decoration-blue-700"
        >
          {source.title}
        </a>
      ))}
    </div>
  );
}

function CompactList({ title, items, tone = "gray" }) {
  if (!Array.isArray(items) || !items.length) return null;
  const titleColor = {
    gray: "text-gray-500",
    red: "text-red-700",
    amber: "text-amber-700",
  }[tone];

  return (
    <div>
      <div
        className={`text-xs font-semibold uppercase tracking-wide ${titleColor}`}
      >
        {title}
      </div>
      <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
        {items.map((item, index) => (
          <li key={`${title}-${index}`}>
            {typeof item === "object" ? JSON.stringify(item) : String(item)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function QuickSearchAiAnalysis({ result }) {
  const analysis = result?.parsedJson;
  const sources = extractGroundingSources(result);

  if (!analysis) {
    return (
      <Alert color="warning">
        <QuickSearchMarkdown>
          {extractModelText(result) || "A IA não retornou uma análise."}
        </QuickSearchMarkdown>
      </Alert>
    );
  }

  const context = analysis.context || {};
  const buildability = analysis.buildability || {};
  const market = analysis.market || {};
  const comparables = Array.isArray(market.comparables)
    ? market.comparables
    : [];
  const possibleExits = Array.isArray(market.possible_exits)
    ? market.possible_exits
    : [];
  const verdictColor = {
    KEEP: "success",
    RESEARCH: "warning",
    DISCARD: "failure",
  }[analysis.verdict] || "gray";
  const buildabilityColor = {
    LIKELY_BUILDABLE: "success",
    UNCERTAIN: "warning",
    LIKELY_NOT_BUILDABLE: "failure",
    NOT_APPLICABLE: "gray",
  }[buildability.status] || "gray";

  return (
    <section className="divide-y divide-gray-200 rounded-xl border border-gray-200 bg-white dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge color={verdictColor}>{analysis.verdict || "RESEARCH"}</Badge>
          <Badge color="gray">{analysis.property_type || "unknown"}</Badge>
        </div>
        <Badge color={buildabilityColor}>
          {buildability.status || "UNCERTAIN"}
        </Badge>
      </div>

      <div className="space-y-2 p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Contexto · {context.classification || "unknown"}
        </div>
        <p className="text-sm leading-6 text-gray-700 dark:text-gray-300">
          {context.summary || "Contexto não determinado."}
        </p>
      </div>

      <div className="space-y-3 p-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Buildability
          </div>
          <p className="mt-1.5 text-sm leading-6 text-gray-700 dark:text-gray-300">
            {buildability.summary || "Não foi possível concluir."}
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <CompactList
            title="Regras verificadas"
            items={buildability.verified_rules}
          />
          <CompactList
            title="Bloqueios"
            items={buildability.blockers}
            tone="red"
          />
          <CompactList
            title="Desconhecido"
            items={buildability.unknowns}
            tone="amber"
          />
        </div>
      </div>

      {(market.summary || comparables.length > 0) && (
        <div className="space-y-3 p-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Mercado próximo
            </div>
            {market.summary && (
              <p className="mt-1.5 text-sm leading-6 text-gray-700 dark:text-gray-300">
                {market.summary}
              </p>
            )}
            {market.land_listing_positioning && (
              <p className="mt-2 text-sm leading-6 text-gray-700 dark:text-gray-300">
                <strong>Posicionamento dos anúncios de terrenos:</strong>{" "}
                {market.land_listing_positioning}
              </p>
            )}
          </div>
          {comparables.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-left text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="pb-2 pr-3 font-semibold">Status</th>
                    <th className="pb-2 pr-3 font-semibold">Imóvel</th>
                    <th className="pb-2 pr-3 font-semibold">Preço</th>
                    <th className="pb-2 pr-3 font-semibold">Tamanho</th>
                    <th className="pb-2 pr-3 font-semibold">Preço unitário</th>
                    <th className="pb-2 pr-3 font-semibold">Data</th>
                    <th className="pb-2 font-semibold">Fonte</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {comparables.map((comparable, index) => (
                    <tr key={`${comparable.address || "comparable"}-${index}`}>
                      <td className="py-2 pr-3">{comparable.status || "—"}</td>
                      <td className="py-2 pr-3">{comparable.address || "—"}</td>
                      <td className="py-2 pr-3">{comparable.price || "—"}</td>
                      <td className="py-2 pr-3">
                        {comparable.lot_or_home_size || "—"}
                      </td>
                      <td className="py-2 pr-3">
                        {comparable.unit_price || "—"}
                      </td>
                      <td className="py-2 pr-3">{comparable.date || "—"}</td>
                      <td className="py-2">{comparable.source || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <CompactList title="Possíveis saídas" items={possibleExits} />
        </div>
      )}

      <div className="grid gap-4 p-4 md:grid-cols-2">
        <CompactList
          title="Alertas críticos"
          items={analysis.critical_flags}
          tone="red"
        />
        <CompactList
          title="Próximas verificações"
          items={analysis.next_checks}
          tone="amber"
        />
      </div>

      {sources.length > 0 && (
        <div className="space-y-2 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Fontes pesquisadas
          </div>
          <SourceLinks sources={sources} />
        </div>
      )}
    </section>
  );
}
